import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Search from "../../../assets/images/search.svg";
import "./ApprovedPlates.scss";
import DataTable from "react-data-table-component";
import { Link, useNavigate } from "react-router-dom";
import { getPlatesByStatus, deletePlate } from "../../../services/platesService";
import { ConfirmModal } from "../../../components/confirm-modal/ConfirmModal";
import {
  flattenProfilesByPlateStatus,
  getPlateDisplayImageUrl,
  getPlateLocationCountry,
  getPlateLocationState,
  getPlateNumber,
  getProfileDisplayName,
  getProfileEmail,
  getProfileUserImage,
  mapPlateStatusLabel,
  getProfileUser,
} from "../../../utils/platesProfileHelpers";

function ApprovedPlates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [plateToDelete, setPlateToDelete] = useState(null);

  const deletePlateMutation = useMutation({
    mutationFn: (plateId) => deletePlate(plateId),
    onSuccess: (data) => {
      toast.success(data?.message || "Plate deleted successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["plates", "approved"] });
      setPlateToDelete(null);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete plate";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    },
  });

  const handleDeleteClick = (row) => setPlateToDelete(row);
  const handleConfirmDelete = () => {
    if (!plateToDelete) return;
    deletePlateMutation.mutate(plateToDelete.id);
  };
  const handleCloseDeleteModal = () => {
    if (!deletePlateMutation.isPending) setPlateToDelete(null);
  };

  // Fetch approved plates
  const {
    data: platesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["plates", "approved"],
    queryFn: () => getPlatesByStatus("approved"),
    retry: 1,
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch approved plates";
      toast.error(errorMessage);
    },
  });

  // One row per plate with status === approved
  const transformedData = useMemo(() => {
    if (!platesData) return [];

    const profiles = platesData.data || platesData.plates || platesData || [];
    const flat = flattenProfilesByPlateStatus(profiles, "approved");

    const formatDate = (dateString) => {
      if (!dateString) return { date: "N/A", time: "N/A" };
      try {
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return { date: dateStr, time: timeStr };
      } catch (e) {
        return { date: dateString, time: "" };
      }
    };

    return flat.map(({ profile, plateEntry, plateId }) => {
      const user = getProfileUser(profile);
      const userName = getProfileDisplayName(profile);
      const userEmail = getProfileEmail(profile);
      const userImage = getProfileUserImage(profile);

      const plateImage =
        getPlateDisplayImageUrl(plateEntry, profile) ||
        process.env.PUBLIC_URL + "/images/plate.svg";

      const approvalDate = plateEntry.updatedAt || profile.updatedAt || profile.createdAt;
      const dateInfo = formatDate(approvalDate);

      const city = getPlateLocationState(plateEntry, profile) || "N/A";
      const country = getPlateLocationCountry(plateEntry, profile) || "N/A";
      const plateNumber = getPlateNumber(plateEntry, profile);

      const verificationType =
        user.kycVerification === "verified" ? "Verified" : "Standard";

      const statusLabel = mapPlateStatusLabel(plateEntry.status);
      const statusClass = (plateEntry.status || "approved").toLowerCase();

      const originalData = { ...profile, plateEntry };

      return {
        id: plateId,
        image: userImage,
        name: userName,
        email: userEmail,
        plateNumber,
        plateImage,
        verificationType,
        datestamp: dateInfo.date,
        timestamp: dateInfo.time,
        city,
        country,
        status: statusLabel,
        statusClass,
        originalData,
      };
    });
  }, [platesData]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return transformedData;

    const searchLower = searchTerm.toLowerCase();
    return transformedData.filter(
      (plate) =>
        plate.name.toLowerCase().includes(searchLower) ||
        plate.email.toLowerCase().includes(searchLower) ||
        plate.plateNumber.toLowerCase().includes(searchLower)
    );
  }, [transformedData, searchTerm]);

  const handleRowClick = (row) => {
    navigate("/plates-management/approved-plates/plate-details", {
      state: { plateData: row.originalData, plateId: row.id },
    });
  };

  const columns = [
    {
      name: "User ID",
      sortable: true,
      grow: 3,
      cell: (row) => (
        <div className="user-cell">
          <img
            src={row.image}
            alt="user"
            className="user-img"
            onError={(e) => {
              e.target.src = process.env.PUBLIC_URL + "/images/user.svg";
            }}
          />
          <div>
            <strong>{row.name}</strong>
            <br />
            <span>{row.email}</span>
          </div>
        </div>
      ),
    },

    {
      name: "Plate Number",
      selector: (row) => row.plateNumber,
      sortable: true,
    },

    {
      name: "Plate Image",
      sortable: false,
      cell: (row) => (
        <div className="plate-image-cell">
          <img
            src={row.plateImage}
            alt="plate"
            className="plate-img"
            onError={(e) => {
              e.target.src = process.env.PUBLIC_URL + "/images/plate.svg";
            }}
          />
        </div>
      ),
    },

    {
      name: "Verification Type",
      selector: (row) => row.verificationType,
      sortable: true,
    },

    {
      name: "Date Approved",
      sortable: true,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.datestamp}</strong>
          <br />
          <span>{row.timestamp}</span>
        </div>
      ),
    },

    {
      name: "Location",
      sortable: false,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.city}</strong>
          <br />
          <span>{row.country}</span>
        </div>
      ),
    },

    {
      name: "Status",
      sortable: true,
      cell: (row) => (
        <span className={`status-badge ${row.statusClass}`}>
          <span className="dot"></span>
          {row.status}
        </span>
      ),
    },

    {
      name: "",
      button: true,
      cell: (row) => (
        <div className="actions">
          <Link
            to="/plates-management/approved-plates/plate-details"
            state={{ plateData: row.originalData, plateId: row.id }}
          >
            <img src={process.env.PUBLIC_URL + "/images/edit.svg"} className="me-2" alt="edit" />
          </Link>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
            disabled={deletePlateMutation.isPending}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            aria-label="Delete plate"
          >
            <img src={process.env.PUBLIC_URL + "/images/trash.svg"} alt="trash" />
          </button>
        </div>
      ),
    },
  ];
  const customStyles = {
    table: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
      },
    },
    headRow: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      },
    },
    headCells: {
      style: {
        fontSize: "10px",
        fontWeight: "500",
        color: "#0D0D0D",
        padding: "12px 10px",
        backgroundColor: "transparent",
      },
    },
    rows: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        fontSize: "10px",
        minHeight: "60px",
        "&:hover": {
          backgroundColor: "#EAE3E0 !important",
          border: "none !important",
          outline: "none !important",
        },
      },
    },
  };

  return (
    <React.Fragment>
      <header className="approved-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <h4 className="title">Approved Plates</h4>
        </div>
        <div className="right-side">
          <div className="search_bar">
            <img src={Search} alt="Search" />
            <input
              type="text"
              placeholder="Search for approved plates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>
      <div className="approvedPlates">
        <div className="card">
          <h4>All Plates</h4>
          {isLoading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Loading approved plates...</p>
            </div>
          ) : isError ? (
            <div className="error-container">
              <p>Error loading approved plates. Please try again.</p>
              {error && (
                <small>
                  {error?.response?.data?.message || error?.message}
                </small>
              )}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
              selectableRows
              customStyles={customStyles}
              onRowClicked={handleRowClick}
              noDataComponent="No approved plates found"
            />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!plateToDelete}
        title="Delete plate"
        message={
          plateToDelete
            ? `Are you sure you want to delete plate ${plateToDelete.plateNumber || plateToDelete.id}? This action cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isConfirmLoading={deletePlateMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteModal}
      />
    </React.Fragment>
  );
}

export default ApprovedPlates;
